/** @format */

import { TextField } from '@mui/material';
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExtranetEditorLanguageDropdown from 'src/components/extranet/ExtranetEditorLanguageDropdown';
import type { ContentEditorPart, ContentPartFileHandlerFn, ContentPartType } from 'src/types';
import { CONTENT_EDITOR_PART_TYPES } from './blockTypes';
import ContentEditorBlockCard from './ContentEditorBlockCard';

interface ContentEditorLanguageOption {
  value?: string;
  market?: string;
}

interface ContentEditorBlocksProps {
  languageOptions: ContentEditorLanguageOption[];
  locale: string;
  defaultLocale: string;
  onLocaleChange: (locale: string) => void;
  title: string;
  titleError?: string;
  partErrors?: Record<string, string>;
  partFieldErrors?: Record<string, { headline?: string; paragraph?: string }>;
  onTitleChange: (title: string) => void;
  parts: ContentEditorPart[];
  onAddPart: (type: ContentPartType) => void;
  onPatchPart: (partId: string | number, patch: Record<string, unknown>) => void;
  onRemovePart: (partId: string | number) => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onReorderPart: (fromId: string | number, toId: string | number) => void;
  onPartFile?: ContentPartFileHandlerFn;
}

export default function ContentEditorBlocks({
  languageOptions,
  locale,
  defaultLocale,
  onLocaleChange,
  title,
  titleError = '',
  partErrors = {},
  partFieldErrors = {},
  onTitleChange,
  parts,
  onAddPart,
  onPatchPart,
  onRemovePart,
  onMoveUp,
  onMoveDown,
  onReorderPart,
  onPartFile = undefined
}: ContentEditorBlocksProps) {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6
      }
    })
  );
  const sortableIds = useMemo(() => parts.map(part => part.id), [parts]);

  // Filter out unique types that can only be added once
  // These types: serviceInformationNo, salesInformationNo, casaNumber should only appear once in contentEditor
  const availablePartTypes = useMemo(() => {
    const uniqueTypes: ContentPartType[] = ['serviceInformationNo', 'salesInformationNo', 'casaNumber'];
    const existingTypes = parts.map(part => part.type);

    return CONTENT_EDITOR_PART_TYPES.filter(({ type }) => {
      if (uniqueTypes.includes(type)) {
        return !existingTypes.includes(type);
      }
      return true;
    });
  }, [parts]);

  return (
    <section
      className="content-editor__section content-editor__section--content"
      aria-labelledby="content-editor-content-heading">
      <div className="content-editor__section-head">
        <h2 className="content-editor__section-title" id="content-editor-content-heading">
          {t('contentEditor.sectionContent')}
        </h2>
        <p className="content-editor__mandatory-hint">
          <span className="content-editor__asterisk">*</span>
          {t('contentEditor.mandatoryFields')}
        </p>
      </div>

      <div className="content-editor__content-fields">
        <ExtranetEditorLanguageDropdown
          languageOptions={languageOptions}
          locale={locale}
          defaultLocale={defaultLocale}
          onLocaleChange={onLocaleChange}
          triggerId="content-editor-language-trigger"
        />
        <div className="content-editor__content-fields-block-wrap">
          <div className="content-editor__content-fields-block bg-gray">
            <TextField
              fullWidth
              required
              variant="outlined"
              margin="normal"
              label={t('contentEditor.title')}
              value={title}
              helperText={titleError || undefined}
              FormHelperTextProps={
                titleError ? { className: 'content-editor__field-error', role: 'alert' } : undefined
              }
              onChange={e => onTitleChange(e.target.value)}
            />
          </div>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={({ active }) => {
            setActiveId(active?.id || null);
            setOverId(active?.id || null);
          }}
          onDragOver={({ over }) => {
            setOverId(over?.id || null);
          }}
          onDragEnd={({ active, over }) => {
            const fromId = active?.id;
            const toId = over?.id;
            if (fromId && toId && fromId !== toId && typeof onReorderPart === 'function') {
              onReorderPart(fromId, toId);
            }
            setActiveId(null);
            setOverId(null);
          }}
          onDragCancel={() => {
            setActiveId(null);
            setOverId(null);
          }}>
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {parts.map((part, index) => (
              <div
                key={part.id}
                className={`content-editor__parts-wrap${
                  activeId && overId === part.id && activeId !== part.id
                    ? ' content-editor__parts-wrap--placeholder'
                    : ''
                }`}>
                <div className="content-editor__parts">
                  <ContentEditorBlockCard
                    part={part}
                    index={index}
                    total={parts.length}
                    error={partErrors[String(part.id)] || ''}
                    fieldErrors={partFieldErrors[String(part.id)]}
                    onPatch={onPatchPart}
                    onRemove={onRemovePart}
                    onMoveUp={onMoveUp}
                    onMoveDown={onMoveDown}
                    onFile={onPartFile}
                  />
                </div>
              </div>
            ))}
          </SortableContext>
        </DndContext>
        <div className="content-editor__add-section-wrap">
          <div className="content-editor__add-section">
            <h3 className="content-editor__add-title">{t('contentEditor.addSectionTitle')}</h3>
            <div className="content-editor__add-grid" role="list">
              {availablePartTypes.map(({ type, Icon }) => (
                <button
                  key={type}
                  type="button"
                  className="content-editor__add-tile"
                  onClick={() => onAddPart(type)}
                  role="listitem">
                  <span className="content-editor__add-tile-icon" aria-hidden>
                    <Icon />
                  </span>
                  <span className="content-editor__add-tile-label">
                    {t(`contentEditor.blockTypes.${type}`, { defaultValue: type })}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
